import {
  GameState,
  StateEffects,
  DayPhase,
  BeachPhase,
  WaveConditions,
  WaveSize,
  WindCondition,
} from "@/types/GameState";
export class GameStateManager {
  private state: GameState;

  constructor() {
    this.state = this.createInitialState();
  }

  private createInitialState(): GameState {
    return {
      day: 1,
      money: 800,
      groupMorale: 70, // Reduced from 80 to make morale management harder
      hangoverLevel: 0,
      energy: 100,
      surfsSoFar: 0,
      partyCount: 0,
      dayPhase: DayPhase.MORNING,
      beachPhase: null,
      timeOfDay: 8, // Start at 8am
      wentToLagosToday: false,
      surfedToday: false,
      wentToBarToday: false, // Track bar visits per day
      currentConditions: null,
      consecutiveHighHangoverDays: 0,
      daysWithLowMorale: 0,
      hasGoneToLagos: false, // Track Lagos trip across all days
      eventsTriggeredToday: [], // Track events triggered today
      lastBreakfastDay: 0, // Track last day someone made breakfast
    };
  }

  getState(): GameState {
    return { ...this.state };
  }

  applyEffects(effects: StateEffects): void {
    if (effects.money !== undefined) {
      this.state.money = Math.max(0, this.state.money + effects.money);
    }
    if (effects.groupMorale !== undefined) {
      this.state.groupMorale = Math.max(
        0,
        Math.min(100, this.state.groupMorale + effects.groupMorale)
      );
    }
    if (effects.hangoverLevel !== undefined) {
      this.state.hangoverLevel = Math.max(
        0,
        Math.min(100, this.state.hangoverLevel + effects.hangoverLevel)
      );
    }
    if (effects.energy !== undefined) {
      this.state.energy = Math.max(
        0,
        Math.min(100, this.state.energy + effects.energy)
      );
    }
    if (effects.surfsSoFar !== undefined) {
      this.state.surfsSoFar += effects.surfsSoFar;
    }
    if (effects.partyCount !== undefined) {
      this.state.partyCount += effects.partyCount;
    }
    if (effects.timeOfDay !== undefined) {
      // Allow time to go past midnight (24:00) up to 30:00 (6am)
      // This allows staying out late with consequences
      this.state.timeOfDay = Math.max(
        0,
        Math.min(30, this.state.timeOfDay + effects.timeOfDay)
      );
    }
  }

  setDayPhase(phase: DayPhase): void {
    this.state.dayPhase = phase;
  }

  setBeachPhase(phase: BeachPhase | null): void {
    this.state.beachPhase = phase;
  }

  goToLagos(): void {
    this.state.wentToLagosToday = true;
    this.state.hasGoneToLagos = true; // Mark that Lagos trip happened
    this.state.surfedToday = false; // Can't surf if you go to Lagos
    this.state.timeOfDay = 23; // Takes whole day
  }

  markSurfed(): void {
    this.state.surfedToday = true;
  }

  markBarVisited(): void {
    this.state.wentToBarToday = true;
  }

  markEventTriggered(eventId: string): void {
    if (!this.state.eventsTriggeredToday.includes(eventId)) {
      this.state.eventsTriggeredToday.push(eventId);
    }
  }

  setLastBreakfastDay(day: number): void {
    this.state.lastBreakfastDay = day;
  }

  generateWaveConditions(): WaveConditions {
    // Random wave size
    const sizeRoll = Math.random();
    let size: WaveSize;
    let gnarliness: number;

    if (sizeRoll < 0.15) {
      size = WaveSize.FLAT;
      gnarliness = 1;
    } else if (sizeRoll < 0.35) {
      size = WaveSize.SMALL;
      gnarliness = 2 + Math.floor(Math.random() * 2);
    } else if (sizeRoll < 0.55) {
      size = WaveSize.MEDIUM;
      gnarliness = 4 + Math.floor(Math.random() * 2);
    } else if (sizeRoll < 0.75) {
      size = WaveSize.GOOD;
      gnarliness = 6 + Math.floor(Math.random() * 2);
    } else if (sizeRoll < 0.9) {
      size = WaveSize.BIG;
      gnarliness = 8 + Math.floor(Math.random() * 2);
    } else {
      size = WaveSize.HUGE;
      gnarliness = 10;
    }

    // Random wind condition
    const windRoll = Math.random();
    let wind: WindCondition;
    if (windRoll < 0.25) {
      wind = WindCondition.OFFSHORE;
    } else if (windRoll < 0.5) {
      wind = WindCondition.LIGHT;
    } else if (windRoll < 0.75) {
      wind = WindCondition.CROSS;
    } else {
      wind = WindCondition.ONSHORE;
    }

    // Generate description
    const sizeDescriptions = {
      [WaveSize.FLAT]: "flat as a lake",
      [WaveSize.SMALL]: "1-2ft ankle slappers",
      [WaveSize.MEDIUM]: "3-4ft fun waves",
      [WaveSize.GOOD]: "5-6ft head high",
      [WaveSize.BIG]: "7-8ft overhead",
      [WaveSize.HUGE]: "10ft+ bombs",
    };

    const windDescriptions = {
      [WindCondition.OFFSHORE]: "clean offshore",
      [WindCondition.LIGHT]: "light winds",
      [WindCondition.CROSS]: "cross-shore chop",
      [WindCondition.ONSHORE]: "messy onshore",
    };

    const description = `${sizeDescriptions[size]}, ${windDescriptions[wind]}`;

    const conditions: WaveConditions = {
      size,
      gnarliness,
      wind,
      description,
    };

    this.state.currentConditions = conditions;
    return conditions;
  }

  getCurrentConditions(): WaveConditions | null {
    return this.state.currentConditions;
  }

  nextDay(): void {
    // Track consecutive high hangover days before recovery
    if (this.state.hangoverLevel >= 80) {
      this.state.consecutiveHighHangoverDays += 1;
    } else {
      this.state.consecutiveHighHangoverDays = 0; // Reset if below threshold
    }

    // Track days with low morale
    if (this.state.groupMorale < 40) {
      this.state.daysWithLowMorale += 1;
    }

    this.state.day += 1;
    // Natural recovery - reduced energy recovery to make energy management harder
    this.state.hangoverLevel = Math.max(0, this.state.hangoverLevel - 20);
    let energyRecovery = 25; // Reduced from 30 to 25

    // Sleep deprivation penalty if stayed out past 4am (28:00)
    if (this.state.timeOfDay > 28) {
      energyRecovery -= 20; // Severe penalty for staying out too late
      this.state.groupMorale = Math.max(0, this.state.groupMorale - 10); // Morale hit
    }

    this.state.energy = Math.min(100, this.state.energy + energyRecovery);
    // Reset daily flags
    this.state.dayPhase = DayPhase.MORNING;
    this.state.beachPhase = null;
    this.state.timeOfDay = 8;
    this.state.wentToLagosToday = false;
    this.state.surfedToday = false;
    this.state.wentToBarToday = false; // Reset bar visit flag
    this.state.currentConditions = null;
    this.state.eventsTriggeredToday = []; // Reset events for new day
  }

  checkFailureConditions(): string | null {
    // Check immediate failure conditions
    if (this.state.groupMorale <= 0) {
      return "GAME OVER: Total group mutiny! Morale hit zero.";
    }
    if (this.state.money <= 0) {
      return "GAME OVER: You're broke! No money left.";
    }
    if (this.state.consecutiveHighHangoverDays >= 3) {
      return "GAME OVER: The lads have been too hungover for too long. Trip abandoned!";
    }
    return null;
  }

  checkVictoryConditions(): { success: boolean; message: string } {
    const state = this.state;

    // Must complete 7 days
    if (state.day < 7) {
      return { success: false, message: "Trip not complete yet!" };
    }

    // Check all victory conditions - HARDER REQUIREMENTS
    const surfGoal = state.surfsSoFar >= 12; // Increased from 10 to 12
    const moraleOk = state.groupMorale >= 50; // Increased from 30 to 50
    const moneyOk = state.money >= 200; // Increased from 100 to 200
    const lagosVisited = state.hasGoneToLagos; // NEW: Must visit Lagos
    const minSurfs = state.surfsSoFar >= 5;

    if (surfGoal && moraleOk && moneyOk && lagosVisited) {
      return {
        success: true,
        message:
          "VICTORY! You successfully managed the surf trip!\n\n✓ 12+ surf sessions\n✓ Morale above 50\n✓ €200+ remaining\n✓ Lagos trip completed",
      };
    } else if (!minSurfs) {
      return {
        success: false,
        message:
          "FAILURE: Fewer than 5 surf sessions. This was supposed to be a surf trip!",
      };
    } else if (!lagosVisited) {
      return {
        success: false,
        message:
          "FAILURE: Never went to Lagos! You can't skip the legendary Lagos trip!",
      };
    } else if (!moraleOk) {
      return {
        success: false,
        message:
          "FAILURE: Group morale too low (below 50). The lads are miserable!",
      };
    } else if (!moneyOk) {
      return {
        success: false,
        message:
          "FAILURE: Not enough money left (below €200). Financial disaster!",
      };
    } else {
      return {
        success: false,
        message:
          "TRIP COMPLETE: You made it through 7 days, but didn't meet all the goals.\n\nAim for 12+ surfs, morale 50+, €200+, and Lagos trip!",
      };
    }
  }

  calculateScore(): { score: number; breakdown: string[] } {
    const state = this.state;
    let score = 0;
    const breakdown: string[] = [];

    // Base score
    const surfPoints = state.surfsSoFar * 100;
    score += surfPoints;
    breakdown.push(`Surf sessions (${state.surfsSoFar}): +${surfPoints}`);

    const moneyPoints = state.money;
    score += moneyPoints;
    breakdown.push(`Money remaining (€${state.money}): +${moneyPoints}`);

    const moralePoints = state.groupMorale * 2;
    score += moralePoints;
    breakdown.push(`Final morale (${state.groupMorale}): +${moralePoints}`);

    const energyPoints = state.energy;
    score += energyPoints;
    breakdown.push(`Final energy (${state.energy}): +${energyPoints}`);

    // Bonuses
    if (state.day >= 7) {
      score += 500;
      breakdown.push("Completed all 7 days: +500");
    }
    if (state.surfsSoFar > 15) {
      score += 1000;
      breakdown.push("More than 15 surfs: +1000");
    }
    if (state.money > 300) {
      score += 500;
      breakdown.push("More than €300 remaining: +500");
    }
    if (state.groupMorale > 80) {
      score += 500;
      breakdown.push("Final morale above 80: +500");
    }
    if (state.partyCount < 4) {
      score += 300;
      breakdown.push("Party count below 4: +300");
    }

    // Penalties
    if (state.daysWithLowMorale > 0) {
      const penalty = state.daysWithLowMorale * 200;
      score -= penalty;
      breakdown.push(
        `Days with morale below 40 (${state.daysWithLowMorale}): -${penalty}`
      );
    }
    if (state.partyCount > 8) {
      score -= 400;
      breakdown.push("Party count above 8: -400");
    }
    if (state.hangoverLevel > 60) {
      score -= 300;
      breakdown.push("Final hangover above 60: -300");
    }

    return { score: Math.max(0, score), breakdown };
  }

  getRankTitle(score: number): string {
    if (score >= 5000) return "Legendary Surf Manager 🏆";
    if (score >= 3500) return "Master of the Waves 🥇";
    if (score >= 2500) return "Competent Coordinator 🥈";
    if (score >= 1500) return "Barely Made It 🥉";
    return "Stick to Day Trips 💀";
  }

  reset(): void {
    this.state = this.createInitialState();
  }
}
